import { Dropdown, Input } from 'ccs-ui-components';
import { isArray } from 'lodash';
import { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { FORM_SERVICE_TYPE, OPERATION_VERB, SERVICE_OPERATION_MAX_TIME_OUT, SERVICE_OPERATION_MAXLENGTH } from '../../../../../../../constants';
import { serviceOpsFormParam, useServiceOpsForm } from '../../../../../../../hooks';
import {
  IEgressEventMappingModel,
  IHeaderParam,
  IIngressEventMappingModel,
  IQueryPathParam,
  IServiceOperation,
  IServiceOperationValidationModel
} from '../../../../../../../models';
import { AppDispatch, useSelector } from '../../../../../../../store/AppStore';
import { updateServiceOperation } from '../../../../../../../store/actions/sagaActions/services/ServiceOperationsSagaActions';
import { addCurrentServiceOperation, deleteCurrentServiceOperation } from '../../../../../../../store/reducer/services/CurrentServiceOperationFormReducer';
import { addServiceOperations } from '../../../../../../../store/reducer/services/ServiceOperationReducer';
import { objToArrForQueryAndHeader } from '../../../../../../../utilities/common/ObjectToArrayUtils';
import { getOperationUrlLabelForServiceOps, getOperationUrlPlaceholderForDB2, getOperationVerbLabel, getOperationVerbList } from '../../../../../../../utilities/services/LabelUtils';
import { isRestDb2MqService } from '../../../../../../../utilities/services/ServiceDetailsUtils';
import { RequestResponseAttribute } from '../request-response-attribute/RequestResponseAttribute';
import HeaderItemWrapper from '../header-param/HeaderItemWrapper';
import QueryItemWrapper from '../query-param/QueryItemWrapper';
import classes from './ServiceOperationForm.module.scss';

interface IAddServiceOpsProps {
  isSaveClicked: boolean;
  onServiceOpsAdded: () => void;
  onServiceOperationEntered: (
    serviceOperationName: string,
    operationURL: string,
    operationVerb: string,
    serviceOperationFormData: IServiceOperationValidationModel
  ) => void;
  onSaveValid: (value: boolean) => void;
  showOpsForm: boolean;
  isEditButtonClick: boolean;
  editViewIndex: number;
  isAddNewServiceOperationClick: boolean;
  viewMode?: boolean;
  serviceEdit?: boolean;
  isViewButtonClick?: boolean;
}

interface IServiceOperationDetailsSignatureInput {
  operationUrl: string;
  operationVerb: string;
  retryErrorCode: string;
  retryCount: string;
  timeOut?: string;
  queryPathParam: IQueryPathParam[];
  headerParam: IHeaderParam[];
  requestAttributes: string[];
  responseAttributes: string[];
}

interface IEditBaselineSnapshot {
  serviceOperationName: string;
  detailsSignature: string;
  ingressSignature: string;
  egressSignature: string;
}

const TIME_OUT_DEFAULT_VALUE = '30000';

const normalizeParamList = (params: any) =>
  (Array.isArray(params) ? [...params] : [])
    .map((item) => ({ key: item.key ?? '', value: item.value ?? '' }))
    .sort((a, b) => {
      if (a.key === b.key) {
        return a.value.localeCompare(b.value);
      }
      return a.key.localeCompare(b.key);
    });

const normalizeStringList = (value: string[] = []) =>
  [...value]
    .map((item) => item ?? '')
    .sort((a, b) => a.localeCompare(b));

const normalizeRetryErrorCode = (value: string) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
    .sort((a, b) => a.localeCompare(b));

const normalizeTimeoutValue = (value?: string) => {
  if (!value || value.trim() === '') {
    return TIME_OUT_DEFAULT_VALUE;
  }
  return value.trim();
};

const getDetailsSignature = (details: IServiceOperationDetailsSignatureInput) =>
  JSON.stringify({
    operationUrl: details.operationUrl ?? '',
    operationVerb: details.operationVerb ?? '',
    retryErrorCode: normalizeRetryErrorCode(details.retryErrorCode ?? ''),
    retryCount: details.retryCount ?? '',
    timeOut: normalizeTimeoutValue(details.timeOut),
    queryPathParam: normalizeParamList(details.queryPathParam),
    headerParam: normalizeParamList(details.headerParam),
    requestAttributes: normalizeStringList(details.requestAttributes),
    responseAttributes: normalizeStringList(details.responseAttributes)
  });

const getIngressEventsSignature = (events: IIngressEventMappingModel[]) =>
  JSON.stringify(
    [...events]
      .map((event) => ({
        eventName: event.eventName ?? '',
        eventVersion: event.eventVersion ?? '',
        reqAttribute: event.reqAttribute ?? '',
        mqRequestMapping: event.mqRequestMapping ?? '',
        payloadToQueryPath: event.payloadToQueryPath ?? '',
        payloadToRequestHeader: event.payloadToRequestHeader ?? '',
        headerToRequestHeader: event.headerToRequestHeader ?? '',
        headerToRequestPayload: event.headerToRequestPayload ?? '',
        requestToAuthenticationPayload: event.requestToAuthenticationPayload ?? '',
        exceptions: [...(event.exceptions ?? [])]
          .map((exception) => ({
            exceptionId: exception.exceptionId ?? '',
            errorCode: exception.errorCode ?? '',
            action: [...(exception.action ?? [])]
              .map((action) => action ?? '')
              .sort((a, b) => a.localeCompare(b))
          }))
          .sort((a, b) => {
            if (a.exceptionId === b.exceptionId) {
              return a.errorCode.localeCompare(b.errorCode);
            }
            return a.exceptionId.localeCompare(b.exceptionId);
          })
      }))
      .sort((a, b) => {
        if (a.eventName === b.eventName) {
          return a.eventVersion.localeCompare(b.eventVersion);
        }
        return a.eventName.localeCompare(b.eventName);
      })
  );

const getEgressEventsSignature = (events: IEgressEventMappingModel[]) =>
  JSON.stringify(
    [...events]
      .map((event) => ({
        ingressName: event.ingressName ?? '',
        ingressEventVersion: event.ingressEventVersion ?? '',
        eventName: event.eventName ?? '',
        eventVersion: event.eventVersion ?? '',
        resAttribute: event.resAttribute ?? '',
        mqResAttribute: event.mqResAttribute ?? '',
        topic: event.topic ?? '',
        partition: event.partition ?? '',
        connection: event.connection ?? ''
      }))
      .sort((a, b) => {
        const ingressNameCompare = a.ingressName.localeCompare(b.ingressName);
        if (ingressNameCompare !== 0) return ingressNameCompare;
        const ingressVersionCompare = a.ingressEventVersion.localeCompare(
          b.ingressEventVersion
        );
        if (ingressVersionCompare !== 0) return ingressVersionCompare;
        const eventNameCompare = a.eventName.localeCompare(b.eventName);
        if (eventNameCompare !== 0) return eventNameCompare;
        return a.eventVersion.localeCompare(b.eventVersion);
      })
  );

const ServiceOperationForm = ({ isSaveClicked, viewMode = false, onServiceOpsAdded, onServiceOperationEntered, onSaveValid, showOpsForm, isEditButtonClick, editViewIndex, isAddNewServiceOperationClick, serviceEdit = false, isViewButtonClick = false }: IAddServiceOpsProps) => {
  const { t: translate } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [opsVerbIndex, setOpsVerbIndex] = useState<number | null>(null);
  const [queryParamList, setQueryParamList] = useState<IQueryPathParam[]>([]);
  const [headerParamList, setHeaderParamList] = useState<IHeaderParam[]>([]);
  const [requestAttributeList, setRequestAttributeList] = useState<string[]>([]);
  const [responseAttributeList, setResponseAttributeList] = useState<string[]>([]);
  const [updateQueryObject, setUpdateQueryObject] = useState<IQueryPathParam>();
  const [updateHeaderObject, setUpdateHeaderObject] = useState<IHeaderParam>();
  const [isEditSaveValid, setIsEditSaveValid] = useState(false);
  const [editFormKey, setEditFormKey] = useState<number>(0);
  const [eventsSaved, setEventsSaved] = useState(false);
  const [editBaselineSnapshot, setEditBaselineSnapshot] = useState<IEditBaselineSnapshot | null>(null);
  const { serviceOperations, importStatus } = useSelector(
    (state) => state.serviceOperationReducer
  );
  const {
    handleInputChange,
    serviceOpsFormData,
    setFormParamValues,
    recheckValidation
  } = useServiceOpsForm(serviceOperations);
  const { ingressEventMappingList } = useSelector(
    (state) => state.ingressEventMappingReducer
  );

  const { egressEventMappingList } = useSelector(
    (state) => state.egressEventMappingReducer
  );

  const { serviceType: reducerServiceType } = useSelector(
    (state) => state.serviceDetailsReducer
  );
  const isEditMode = !!(isEditButtonClick || serviceEdit) && !isAddNewServiceOperationClick;

  // Track when ingress/egress events are saved
  useEffect(() => {
    if (ingressEventMappingList.length > 0 || egressEventMappingList.length > 0) {
      setEventsSaved(true);
    }
  }, [ingressEventMappingList, egressEventMappingList]);

  const HELPER_TEXT = {
    name_ht: translate('helper_text.txt_name_ht'),
    operation_uri_ht: translate('helper_text.txt_operation_uri_ht'),
  };

  const SERVICE_OPERATIONS_ERRORS = {
    serviceOperation: translate(
      'service_operation_details_form.serviceOperation'
    ),
    serviceOperationExist: translate(
      'service_operation_details_form.serviceOperationExist'
    ),
    serviceOperationDuplicateError: translate(
      'service_operation_details_form.serviceOperationDuplicateError'
    ),
    operationURL: translate('service_operation_details_form.operationURL'),
    invalidKey: translate('service_operation_details_form.invalidKey'),
    invalidValue: translate('service_operation_details_form.invalidValue'),
    queryKeyExists: translate('service_operation_details_form.queryKeyExists'),
    headerKeyExists: translate('service_operation_details_form.headerKeyExists')
  };

  const OPERATION_VERB_NEW = getOperationVerbList(reducerServiceType, translate);
  /**
   * Checks if query key and header key already exists or not
   */
  useEffect(() => {
    const newFormData = { ...serviceOpsFormData };
    let status = false;
    if (isArray(queryParamList)) {
      queryParamList.forEach((item) => {
        if (serviceOpsFormData.queryParamKey.value === item.key) {
          status = true;
        }
        return item;
      });
    }
    const regExp = newFormData.queryParamKey.expression as RegExp;
    if (status) {
      newFormData.queryParamKey.isValid = false;
      newFormData.queryParamKey.isTouched = false;
      newFormData.queryParamKey.error = SERVICE_OPERATIONS_ERRORS.queryKeyExists;
    } else if (
      !status
      && regExp.test(newFormData.queryParamKey.value)
      && newFormData.queryParamKey.value.length
      < parseInt(SERVICE_OPERATION_MAXLENGTH.queryPathParamMaxLength, 10)
    ) {
      newFormData.queryParamKey.isValid = true;
      newFormData.queryParamKey.isTouched = true;
      newFormData.queryParamKey.error = '';
    }
    setFormParamValues(newFormData);
  }, [
    serviceOpsFormData.queryParamKey.value,
    queryParamList?.length,
    queryParamList
  ]);

  useEffect(() => {
    // set data into form on view button click
    const newFormData = { ...serviceOpsFormData };
    if (isViewButtonClick && serviceOperations.length > 0) {
      const serviceOperation = serviceOperations[editViewIndex];
      newFormData.serviceOperationName.value = serviceOperation.serviceOperation;
      newFormData.serviceOperationName.isValid = true;
      newFormData.serviceOperationName.isTouched = false;
      newFormData.operationVerb.value = serviceOperation.operationVerb;
      const index = OPERATION_VERB_NEW.type.findIndex((item) => item.name.toLocaleUpperCase() === serviceOperation.operationVerb?.toLocaleUpperCase());
      newFormData.operationURL.value = serviceOperation.operationUrl;
      newFormData.retryErrorCode.value = serviceOperation.retry?.errorCode.join(', ') || '';
      newFormData.retryCount.value = serviceOperation.retry?.count || '';
      newFormData.timeOut.value = serviceOperation?.timeOut || TIME_OUT_DEFAULT_VALUE;
      setOpsVerbIndex(index);
      const queryArr = serviceOperation.queryPathParam !== null
        ? objToArrForQueryAndHeader(serviceOperation.queryPathParam)
        : {};
      const headerArr = serviceOperation.queryPathParam !== null
        ? objToArrForQueryAndHeader(serviceOperation.headerParam)
        : {};
      setHeaderParamList(headerArr as IHeaderParam[]);
      setQueryParamList(queryArr as IQueryPathParam[]);
      setRequestAttributeList(serviceOperation?.requestAttributes ?? []);
      setResponseAttributeList(serviceOperation?.responseAttributes ?? []);
      setFormParamValues(newFormData);
    }
  }, [isViewButtonClick, serviceOperations]);

  useEffect(() => {
    const newFormData = { ...serviceOpsFormData };
    const status = isArray(headerParamList) && headerParamList?.some((item) => serviceOpsFormData.headerParamKey.value === item.key);
    const regExp = newFormData.headerParamKey.expression as RegExp;
    if (status) {
      newFormData.headerParamKey.isValid = false;
      newFormData.headerParamKey.isTouched = false;
      newFormData.headerParamKey.error = SERVICE_OPERATIONS_ERRORS.headerKeyExists;
    } else if (
      !status
      && regExp.test(newFormData.headerParamKey.value)
      && newFormData.headerParamKey.value.length
      < parseInt(SERVICE_OPERATION_MAXLENGTH.headerParamMaxLength, 10)
    ) {
      newFormData.headerParamKey.isValid = true;
      newFormData.headerParamKey.isTouched = true;
      newFormData.headerParamKey.error = '';
    }
    setFormParamValues(newFormData);
  }, [
    serviceOpsFormData.headerParamKey.value,
    headerParamList?.length,
    headerParamList
  ]);

  /**
   Add, Cancel, delete, update on query items
   */
  const onAddQueryHandler = () => {
    const tempQuery = [...queryParamList];
    const tempQueryObj = {
      key: serviceOpsFormData.queryParamKey.value,
      value: serviceOpsFormData.queryParamValue.value,
      isEdit: false
    };
    tempQuery.push(tempQueryObj);
    setQueryParamList(tempQuery);
    serviceOpsFormData.queryParamKey.value = '';
    serviceOpsFormData.queryParamValue.value = '';
  };

  const onQueryItemChange = (obj: IQueryPathParam) => {
    setUpdateQueryObject(obj);
  };

  const onQueryItemDelete = (index: number) => {
    const queryData = [...queryParamList];
    queryData.splice(index, 1);
    setQueryParamList(queryData);
  };

  useEffect(() => {
    const tempIndex = updateQueryObject?.index ? updateQueryObject.index : 0;
    if (updateQueryObject) {
      const tempQueryParamList = [...queryParamList];
      tempQueryParamList.splice(tempIndex, 1, updateQueryObject);
      setQueryParamList(tempQueryParamList);
    }
  }, [updateQueryObject]);

  useEffect(() => {
    if (isAddNewServiceOperationClick) {
      const newFormData = { ...serviceOpsFormParam };
      const fields = ['serviceOperationName', 'operationVerb', 'operationURL', 'retryErrorCode', 'retryCount', 'timeOut', 'queryParamKey', 'queryParamValue', 'headerParamKey', 'headerParamValue', 'requestAttributes', 'responseAttributes'];
      fields.forEach((field) => {
        const key = field as keyof IServiceOperationValidationModel;
        newFormData[key].value = '';
        newFormData[key].isValid = true;
      });
      // Set default timeout value
      newFormData.timeOut.value = TIME_OUT_DEFAULT_VALUE;
      newFormData.serviceOperationName.isTouched = false;
      setOpsVerbIndex(null);
      setHeaderParamList([]);
      setQueryParamList([]);
      setRequestAttributeList([]);
      setResponseAttributeList([]);
      serviceOpsFormData.queryParamKey.value = '';
      serviceOpsFormData.queryParamValue.value = '';
      serviceOpsFormData.headerParamKey.value = '';
      serviceOpsFormData.headerParamValue.value = '';
      setFormParamValues(newFormData);
      // Reset events saved flag when adding new service operation
      setEventsSaved(false);
      setEditBaselineSnapshot(null);
    }
  }, [isAddNewServiceOperationClick]);

  useEffect(() => {
    const newFormData = { ...serviceOpsFormData };
    if ((isEditButtonClick || serviceEdit) && !isAddNewServiceOperationClick) {
      // Increment the key to force RequestResponseAttribute component to remount
      setEditFormKey((prev) => prev + 1);
      // Reset events saved flag when editing
      setEventsSaved(false);

      const serviceOperation = serviceOperations[editViewIndex];
      newFormData.serviceOperationName.value = serviceOperation.serviceOperation;
      newFormData.serviceOperationName.isValid = true;
      newFormData.serviceOperationName.isTouched = false;
      newFormData.operationVerb.value = serviceOperation.operationVerb || '';
      const index = OPERATION_VERB_NEW.type.findIndex(
        (item) => item.name === serviceOperation.operationVerb
      );
      setOpsVerbIndex(index);
      setQueryParamList(
        objToArrForQueryAndHeader(serviceOperation.queryPathParam)
      );
      setHeaderParamList(
        objToArrForQueryAndHeader(serviceOperation.headerParam)
      );

      setRequestAttributeList(serviceOperation?.requestAttributes ?? []);
      setResponseAttributeList(serviceOperation?.responseAttributes ?? []);
      setEditBaselineSnapshot({
        serviceOperationName: serviceOperation.serviceOperation,
        detailsSignature: getDetailsSignature({
          operationUrl: serviceOperation.operationUrl || '',
          operationVerb: serviceOperation.operationVerb || '',
          retryErrorCode: serviceOperation.retry?.errorCode.join(', ') || '',
          retryCount: serviceOperation.retry?.count || '',
          timeOut: serviceOperation?.timeOut || TIME_OUT_DEFAULT_VALUE,
          queryPathParam: objToArrForQueryAndHeader(
            serviceOperation.queryPathParam
          ) as IQueryPathParam[],
          headerParam: objToArrForQueryAndHeader(
            serviceOperation.headerParam
          ) as IHeaderParam[],
          requestAttributes: serviceOperation?.requestAttributes ?? [],
          responseAttributes: serviceOperation?.responseAttributes ?? []
        }),
        ingressSignature: getIngressEventsSignature(
          ingressEventMappingList.filter(
            (item) =>
              item.serviceOperationName === serviceOperation.serviceOperation
          )
        ),
        egressSignature: getEgressEventsSignature(
          egressEventMappingList.filter(
            (item) =>
              item.serviceOperationName === serviceOperation.serviceOperation
          )
        )
      });

      // Reset all form fields to clean state
      const resetField = (fieldName: keyof IServiceOperationValidationModel, value = '') => {
        newFormData[fieldName].value = value;
        newFormData[fieldName].isValid = true;
        newFormData[fieldName].isTouched = false;
        newFormData[fieldName].error = '';
      };

      // Reset all fields systematically
      resetField('operationURL', serviceOperation.operationUrl || '');
      resetField('retryErrorCode', serviceOperation.retry?.errorCode.join(', ') || '');
      resetField('retryCount', serviceOperation.retry?.count || '');
      resetField('timeOut', serviceOperation?.timeOut || TIME_OUT_DEFAULT_VALUE);
      resetField('requestAttributes');
      resetField('responseAttributes');
      resetField('security');
      resetField('version');
      resetField('queryParamKey');
      resetField('queryParamValue');
      resetField('headerParamKey');
      resetField('headerParamValue');

      setFormParamValues(newFormData);
      if (importStatus) {
        const recheckArray: string[] = [];
        recheckArray.push('serviceOperationName');
        if (
          serviceOperation.operationUrl
          || (!serviceOperation.operationUrl && reducerServiceType === 'REST')
        ) recheckArray.push('operationURL');
        recheckValidation(recheckArray);
      }
    }
  }, [isEditButtonClick, serviceEdit]);

  /**
   Add, Cancel, delete, update on header items
   */
  useEffect(() => {
    onServiceOperationEntered(
      serviceOpsFormData.serviceOperationName.value,
      serviceOpsFormData.operationURL.value,
      serviceOpsFormData.operationVerb.value,
      serviceOpsFormData
    );
  }, [
    serviceOpsFormData.serviceOperationName.value,
    serviceOpsFormData.operationURL.value,
    serviceOpsFormData.operationVerb.value
  ]);

  const onAddHeaderHandler = () => {
    const tempHeader = [...headerParamList];
    const tempHeaderObj = {
      key: serviceOpsFormData.headerParamKey.value,
      value: serviceOpsFormData.headerParamValue.value,
      isEdit: false
    };
    tempHeader.push(tempHeaderObj);
    setHeaderParamList(tempHeader);
    serviceOpsFormData.headerParamKey.value = '';
    serviceOpsFormData.headerParamValue.value = '';
  };

  const onHeaderItemChange = (obj: IHeaderParam) => {
    setUpdateHeaderObject(obj);
  };

  const onHeaderItemDelete = (index: number) => {
    const headerData = [...headerParamList];
    headerData.splice(index, 1);
    setHeaderParamList(headerData);
  };

  const updateHeaderParamList = (headerParamsItem: IHeaderParam[]) => {
    setHeaderParamList(headerParamsItem);
  };

  useEffect(() => {
    const tempIndex = updateHeaderObject?.index ? updateHeaderObject.index : 0;
    if (updateHeaderObject) {
      const tempHeaderParamList = [...headerParamList];
      tempHeaderParamList.splice(tempIndex, 1, updateHeaderObject);
      setHeaderParamList(tempHeaderParamList);
    }
  }, [updateHeaderObject]);

  const operationVerbChangeHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const serviceTypes = [FORM_SERVICE_TYPE.DB2, FORM_SERVICE_TYPE.MQ];
    const index = parseInt(e.target.value, 10);
    setOpsVerbIndex(index);
    const newFormData = { ...serviceOpsFormData };
    newFormData.operationVerb.value = OPERATION_VERB_NEW.type[index]?.name;
    if (serviceTypes.includes(reducerServiceType.toLocaleLowerCase())) {
      newFormData.headerParamKey.value = '';
      newFormData.operationURL.value = '';
      newFormData.headerParamValue.value = '';
      newFormData.queryParamKey.value = '';
      newFormData.queryParamValue.value = '';
      setHeaderParamList([]);
      setQueryParamList([]);
    }
    setFormParamValues(newFormData);
  };

  useEffect(() => {
    // for soap service set default operation verb as POST
    if (reducerServiceType?.toLocaleLowerCase() === FORM_SERVICE_TYPE.SOAPSERVICE) {
      setOpsVerbIndex(0);
      const newFormData = { ...serviceOpsFormData };
      newFormData.operationVerb.value = OPERATION_VERB_NEW.type[0].name;
      setFormParamValues(newFormData);
    }
  }, [reducerServiceType]);

  const isIngressEventExistForServiceOps = () =>
    ingressEventMappingList.some(
      (event: IIngressEventMappingModel) =>
        event.serviceOperationName
        === serviceOpsFormData.serviceOperationName.value
    );

  // Validating based on service type
  const isExternalServiceTypeValid = () =>
    reducerServiceType?.toLocaleLowerCase() === FORM_SERVICE_TYPE.EXTERNAL
    && serviceOpsFormData.serviceOperationName.isValid;

  const isSoapServiceTypeValid = () =>
    reducerServiceType?.toLocaleLowerCase() === FORM_SERVICE_TYPE.SOAPSERVICE
    && serviceOpsFormData.serviceOperationName.isValid
    && serviceOpsFormData.operationVerb.value.toLocaleLowerCase()
    === OPERATION_VERB_NEW.type[0].name.toLocaleLowerCase();

  const isBusinessServiceTypeValid = () =>
    reducerServiceType?.toLocaleLowerCase() === FORM_SERVICE_TYPE.REST
    && serviceOpsFormData.serviceOperationName.isValid
    && serviceOpsFormData.operationURL.isValid
    && serviceOpsFormData.serviceOperationName.value !== ''
    && serviceOpsFormData.operationURL.value !== ''
    && serviceOpsFormData.operationVerb.value !== '';

  // This function performs validation based on service type. Also, enables or disables edit save button
  const validateForm = () => {
    if (isExternalServiceTypeValid() || isSoapServiceTypeValid()) {
      setIsEditSaveValid(true);
      if (
        serviceOpsFormData.operationURL.value === ''
        || (serviceOpsFormData.operationURL.value
          && serviceOpsFormData.operationURL.isValid)
      ) {
        serviceOpsFormData.operationURL.isValid = true;
        setIsEditSaveValid(true);
      } else {
        setIsEditSaveValid(false);
      }
    } else if (isBusinessServiceTypeValid()) {
      setIsEditSaveValid(true);
    } else {
      setIsEditSaveValid(false);
    }
    return isEditSaveValid;
  };

  const isDB2OrMQ = () =>
    (reducerServiceType?.toLocaleLowerCase() === FORM_SERVICE_TYPE.DB2
      || reducerServiceType?.toLocaleLowerCase() === FORM_SERVICE_TYPE.MQ)
    && serviceOpsFormData.serviceOperationName.isValid
    && serviceOpsFormData.serviceOperationName.value !== ''
    && serviceOpsFormData.operationURL.value !== ''
    && serviceOpsFormData.operationVerb.value !== '';

  // This useEffect enables or disables save button based on validation
  useEffect(() => {
    // Check if any fields have been touched/modified by the user
    const hasFieldChanges = serviceOpsFormData.retryErrorCode.isTouched
      || serviceOpsFormData.retryCount.isTouched
      || serviceOpsFormData.timeOut.isTouched;

    // Check if ingress/egress events exist for validation
    const hasIngressEgressEvents = isIngressEventExistForServiceOps();

    let shouldValidate = false;
    if (
      (isExternalServiceTypeValid()
        && hasIngressEgressEvents
        && isEditSaveValid)
      || ((isBusinessServiceTypeValid()
        || isSoapServiceTypeValid()
        || isDB2OrMQ())
        && hasIngressEgressEvents)
    ) {
      shouldValidate = false;
    } else {
      shouldValidate = true;
    }
    const hasRetryErrorCodeVal = serviceOpsFormData.retryErrorCode.value;
    const hasRetryCountVal = serviceOpsFormData.retryCount.value;
    const hasTimeOutVal = serviceOpsFormData.timeOut.value;
    const hasRetryErrorCodeValid = serviceOpsFormData.retryErrorCode.isValid;
    const hasRetryCountValid = serviceOpsFormData.retryCount.isValid;
    const hasTimeOutValid = serviceOpsFormData.timeOut.isValid;
    // Check retry configuration (error code and retry count must be both empty or both filled and valid)
    const isRetryConfigValid = (!hasRetryErrorCodeVal && !hasRetryCountVal)
      || (hasRetryErrorCodeVal && hasRetryCountVal && hasRetryErrorCodeValid && hasRetryCountValid);

    // Check timeout independently
    const isTimeoutValid = !hasTimeOutVal || hasTimeOutValid;

    const operationNameForComparison = editBaselineSnapshot?.serviceOperationName
      || serviceOperations[editViewIndex]?.serviceOperation
      || serviceOpsFormData.serviceOperationName.value;
    const currentDetailsSignature = getDetailsSignature({
      operationUrl: serviceOpsFormData.operationURL.value || '',
      operationVerb: serviceOpsFormData.operationVerb.value || '',
      retryErrorCode: serviceOpsFormData.retryErrorCode.value || '',
      retryCount: serviceOpsFormData.retryCount.value?.toString() || '',
      timeOut: serviceOpsFormData.timeOut.value,
      queryPathParam: queryParamList,
      headerParam: headerParamList,
      requestAttributes: requestAttributeList,
      responseAttributes: responseAttributeList
    });
    const currentIngressSignature = getIngressEventsSignature(
      ingressEventMappingList.filter(
        (item) => item.serviceOperationName === operationNameForComparison
      )
    );
    const currentEgressSignature = getEgressEventsSignature(
      egressEventMappingList.filter(
        (item) => item.serviceOperationName === operationNameForComparison
      )
    );
    const hasDetailsChangesInEdit = isEditMode
      && !!editBaselineSnapshot
      && currentDetailsSignature !== editBaselineSnapshot.detailsSignature;
    const hasEventAssociationChangesInEdit = isEditMode
      && !!editBaselineSnapshot
      && (currentIngressSignature !== editBaselineSnapshot.ingressSignature
        || currentEgressSignature !== editBaselineSnapshot.egressSignature);
    const hasValidChangesInEdit = hasDetailsChangesInEdit || hasEventAssociationChangesInEdit;

    if (isEditMode) {
      if (hasValidChangesInEdit && isRetryConfigValid && isTimeoutValid) {
        onSaveValid(shouldValidate);
      } else {
        onSaveValid(true); // Disable save button
      }
    } else if (
      (hasFieldChanges || eventsSaved) && isRetryConfigValid && isTimeoutValid
    ) {
      // Keep create flow behavior: save requires touched fields or new events.
      onSaveValid(shouldValidate);
    } else {
      onSaveValid(true); // Disable save button
    }
  }, [
    serviceOpsFormData,
    queryParamList,
    headerParamList,
    requestAttributeList,
    responseAttributeList,
    ingressEventMappingList,
    egressEventMappingList,
    serviceOperations,
    editViewIndex,
    isEditMode,
    reducerServiceType,
    isEditSaveValid,
    eventsSaved,
    editBaselineSnapshot
  ]);

  // Performing validation when form data changes
  useEffect(() => {
    validateForm();
  }, [serviceOpsFormData]);

  /**
   * Dispatching the data on click of Save service ops button
   */
  useEffect(() => {
    const data: IServiceOperation = {
      serviceOperation: serviceOpsFormData.serviceOperationName.value,
      operationUrl: serviceOpsFormData.operationURL.value,
      retry: {
        errorCode: serviceOpsFormData.retryErrorCode.value ? serviceOpsFormData.retryErrorCode.value.split(',').map((num) => num.trim()) : [],
        count: serviceOpsFormData.retryCount.value.toString()
      },
      timeOut: serviceOpsFormData.timeOut.value && serviceOpsFormData.timeOut.value.trim() !== ''
        ? serviceOpsFormData.timeOut.value.toString()
        : TIME_OUT_DEFAULT_VALUE,
      operationVerb:
        opsVerbIndex === 0 || opsVerbIndex
          ? OPERATION_VERB_NEW.type[opsVerbIndex]?.name
          : '',
      requestAttributes: requestAttributeList?.length ? requestAttributeList : [],
      responseAttributes: responseAttributeList?.length ? responseAttributeList : [],
      security: serviceOpsFormData.security.value,
      queryPathParam: queryParamList?.length ? queryParamList : [],
      headerParam: headerParamList?.length ? headerParamList : []
    };

    if (isSaveClicked && !isEditButtonClick) {
      dispatch(addServiceOperations(data));
      onServiceOpsAdded();
    } else if (isSaveClicked && isEditButtonClick) {
      dispatch(
        updateServiceOperation({
          index: editViewIndex,
          serviceOperation: data,
          statusMsg: 'updated'
        })
      );
      onServiceOpsAdded();
    }
  }, [serviceOpsFormData, isSaveClicked]);

  /**
   * Checks if the service operation already exists or not
   */
  useEffect(() => {
    const newFormData = { ...serviceOpsFormData };
    serviceOperations.forEach((name: IServiceOperation) => {
      if (
        serviceOpsFormData.serviceOperationName.value
        === name.serviceOperation
        && !isEditButtonClick
      ) {
        newFormData.serviceOperationName.isValid = false;
        newFormData.serviceOperationName.error = SERVICE_OPERATIONS_ERRORS.serviceOperationExist;
      } else if (
        serviceOpsFormData.serviceOperationName.value
        === name.serviceOperation
        && isEditButtonClick
      ) {
        newFormData.serviceOperationName.isValid = true;
        newFormData.serviceOperationName.isTouched = true;
      }
      return newFormData;
    });
    setFormParamValues(newFormData);
  }, [serviceOperations, serviceOpsFormData.serviceOperationName]);

  /**
   * Dispatch to reducer only when hiding the form
   */
  useEffect(() => {
    const FormData: IServiceOperation = {
      serviceOperation: serviceOpsFormData.serviceOperationName.value,
      operationUrl: serviceOpsFormData.operationURL.value,
      operationVerb:
        opsVerbIndex === 0 || opsVerbIndex
          ? OPERATION_VERB_NEW.type[opsVerbIndex]?.name
          : '',
      retry: {
        errorCode: serviceOpsFormData.retryErrorCode.value.split(',').map((num) => num.trim()),
        count: serviceOpsFormData.retryCount.value.toString()
      },
      timeOut: serviceOpsFormData.timeOut.value && serviceOpsFormData.timeOut.value.trim() !== ''
        ? serviceOpsFormData.timeOut.value.toString()
        : TIME_OUT_DEFAULT_VALUE,
      requestAttributes: requestAttributeList,
      responseAttributes: responseAttributeList,
      security: serviceOpsFormData.security.value,
      queryPathParam: queryParamList,
      headerParam: headerParamList
    };
    if (!showOpsForm && !isSaveClicked) {
      dispatch(addCurrentServiceOperation(FormData));
    }
    if (isSaveClicked) {
      dispatch(deleteCurrentServiceOperation());
    }
  }, [showOpsForm, isSaveClicked]);

  useEffect(() => {
    const newFormParams = { ...serviceOpsFormData };
    newFormParams.serviceOperationName.isValid = true;
    // Set default timeout value if empty
    if (!newFormParams.timeOut.value || newFormParams.timeOut.value.trim() === '') {
      newFormParams.timeOut.value = TIME_OUT_DEFAULT_VALUE;
    }
    setFormParamValues(newFormParams);
  }, []);

  const shouldDisplayHeaderParam = () =>
    (serviceOpsFormParam.operationVerb.value?.toLocaleLowerCase()
      !== `${OPERATION_VERB[5].name?.toLocaleLowerCase()}`
      && serviceOpsFormParam.operationVerb.value?.toLocaleLowerCase()
      !== `${OPERATION_VERB[6].name?.toLocaleLowerCase()}`)
    || reducerServiceType?.toLocaleLowerCase() !== FORM_SERVICE_TYPE.DB2;

  const shouldDisplayQueryParam = () =>
    reducerServiceType?.toLocaleLowerCase() !== FORM_SERVICE_TYPE.MQ;

  const shouldDisplayRequestResponseAttributes = () =>
    reducerServiceType?.toLocaleLowerCase() === FORM_SERVICE_TYPE.REST;

  const responseDataUpdateHandler = (responseData: any) => {
    if (responseData) {
      setResponseAttributeList(responseData);
    }
  };

  const requestDataUpdateHandler = (requestData: any) => {
    if (requestData) {
      setRequestAttributeList(requestData);
    }
  };

  const handleTimeoutBlur = () => {
    // Restore default value if empty
    if (!serviceOpsFormData.timeOut.value || serviceOpsFormData.timeOut.value.trim() === '') {
      const newFormData = { ...serviceOpsFormData };
      newFormData.timeOut.value = TIME_OUT_DEFAULT_VALUE;
      newFormData.timeOut.isValid = true;
      newFormData.timeOut.error = '';
      setFormParamValues(newFormData);
    }
  };

  return (
    <div>
      {showOpsForm && (
        <div className={classes['main-container']}>
          <Row className={classes['form-container']}>
            <Form>
              <Row className="mb-3">
                <Col sm={8}>
                  <Form.Group controlId="service-operation-name">
                    <Input
                      isMandatory
                      labelText={String(translate('service_operation_form.service_operation_name'))}
                      infoIconTxt={String(translate('service_operation_form.srvc_ops_name_info_bubble'))}
                      label=""
                      placeholder={String(
                        translate(
                          'service_operation_form.plchd_service_operation_name'
                        )
                      )}
                      name="serviceOperationName"
                      error={!serviceOpsFormData.serviceOperationName.isValid}
                      onChange={handleInputChange}
                      value={serviceOpsFormData.serviceOperationName.value}
                      maxLength={Number(
                        SERVICE_OPERATION_MAXLENGTH.serviceOpsName
                      )}
                      data-testid="so-input-name-field"
                      helperText={HELPER_TEXT.name_ht}
                      errorTxt={serviceOpsFormData.serviceOperationName.error}
                      disabled={
                        (isEditButtonClick && !importStatus)
                        || (viewMode && !isAddNewServiceOperationClick)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col sm={4}>
                  <Form.Group controlId="operationVerb">
                    <Dropdown
                      isMandatory={reducerServiceType?.toLocaleLowerCase() !== FORM_SERVICE_TYPE.EXTERNAL}
                      labelText={getOperationVerbLabel(reducerServiceType, translate)}
                      infoIconTxt={OPERATION_VERB_NEW.translate}
                      label=""
                      onChange={operationVerbChangeHandler}
                      value={opsVerbIndex?.toString()}
                      data={OPERATION_VERB_NEW.type}
                      name="operationVerb"
                      data-testid="operation-verb"
                      className={classes['dropdown-control']}
                      disabled={
                        (viewMode
                          && !isAddNewServiceOperationClick
                          && !isEditButtonClick
                          && !serviceEdit)
                        || reducerServiceType?.toLocaleLowerCase()
                        === FORM_SERVICE_TYPE.SOAPSERVICE
                      }
                      placeholder={String(
                        translate('fsm_registry_form.lbl_placeholder_select')
                      )}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group controlId="operationURL">
                <Input
                  isMandatory={isRestDb2MqService(reducerServiceType)}
                  labelText={getOperationUrlLabelForServiceOps(reducerServiceType, serviceOpsFormParam.operationVerb.value, translate)}
                  infoIconTxt={String(translate('service_operation_form.op_uri_info_bubble'))}
                  label=""
                  placeholder={String(
                    getOperationUrlPlaceholderForDB2(
                      reducerServiceType,
                      serviceOpsFormParam.operationVerb.value,
                      translate
                    )
                  )}
                  onChange={handleInputChange}
                  name="operationURL"
                  value={serviceOpsFormData.operationURL.value}
                  maxLength={Number(
                    SERVICE_OPERATION_MAXLENGTH.opsURIMaxLength
                  )}
                  error={!serviceOpsFormData.operationURL.isValid}
                  data-testid="ops-url"
                  helperText={HELPER_TEXT.operation_uri_ht}
                  errorTxt={serviceOpsFormData.operationURL.error}
                  disabled={
                    viewMode
                    && !isAddNewServiceOperationClick
                    && !isEditButtonClick
                    && !serviceEdit
                  }
                />
              </Form.Group>
              {reducerServiceType?.toLocaleLowerCase() === 'rest' && (
                <>
                  <Row className="mt-3">
                    <Col sm={8}>
                      <Form.Group controlId="retry-errorcode-list">
                        <Input
                          labelText="Retry Error code"
                          infoIconTxt="Please enter list of error codes for which you want to retry. Make sure to enter the error codes comma seperated. Eg. 400, 500, 409"
                          label=""
                          placeholder="Eg. 400, 500, 409"
                          name="retryErrorCode"
                          error={!serviceOpsFormData.retryErrorCode.isValid}
                          onChange={handleInputChange}
                          value={serviceOpsFormData.retryErrorCode.value}
                          maxLength={Number(
                            SERVICE_OPERATION_MAXLENGTH.serviceOpsName
                          )}
                          data-testid="retry-error-code-field"
                          helperText="(Start with error code and keep adding list of error codes with comma as seperator)"
                          errorTxt={serviceOpsFormData.retryErrorCode.error}
                          disabled={
                            viewMode
                            && !isAddNewServiceOperationClick
                            && !isEditButtonClick
                            && !serviceEdit
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={4}>
                      <Form.Group controlId="retryCount">
                        <Input
                          labelText="Retry Count"
                          infoIconTxt="This field will contain number of retries"
                          label=""
                          placeholder="Eg. 4"
                          name="retryCount"
                          error={!serviceOpsFormData.retryCount.isValid}
                          onChange={handleInputChange}
                          value={serviceOpsFormData.retryCount.value}
                          maxLength={Number(
                            SERVICE_OPERATION_MAXLENGTH.serviceOpsName
                          )}
                          data-testid="retry-count-field"
                          helperText="(Only numbers from 0-9 are allowed as valid characters)"
                          errorTxt={serviceOpsFormData.retryCount.error}
                          disabled={
                            viewMode
                            && !isAddNewServiceOperationClick
                            && !isEditButtonClick
                            && !serviceEdit
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    <Col sm={8}>
                      <Form.Group controlId="retry-errorcode-list">
                        <Input
                          type="number"
                          max={String(SERVICE_OPERATION_MAX_TIME_OUT)}
                          labelText="Time Out"
                          infoIconTxt="Please enter the timeout value in milliseconds. Eg. 30000"
                          label=""
                          placeholder="Eg. 1000"
                          name="timeOut"
                          error={!serviceOpsFormData.timeOut.isValid}
                          onChange={handleInputChange}
                          onBlur={handleTimeoutBlur}
                          value={serviceOpsFormData.timeOut.value}
                          maxLength={Number(
                            SERVICE_OPERATION_MAXLENGTH.timeOutMaxLength
                          )}
                          data-testid="time-out-field"
                          helperText="(Only numbers from 0-9 are allowed as valid characters. Maximum value: 60000 milliseconds)"
                          errorTxt={serviceOpsFormData.timeOut.error}
                          disabled={
                            viewMode
                            && !isAddNewServiceOperationClick
                            && !isEditButtonClick
                            && !serviceEdit
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}
              {reducerServiceType?.toLocaleLowerCase()
                !== FORM_SERVICE_TYPE.SOAPSERVICE && (
                  <>
                    {reducerServiceType?.toLocaleLowerCase()
                      !== FORM_SERVICE_TYPE.MQ && (
                        <QueryItemWrapper
                          key={`query-${editFormKey}`}
                          handleInputChange={handleInputChange}
                          serviceOpsFormData={serviceOpsFormData}
                          onAddQueryHandler={onAddQueryHandler}
                          queryParamList={queryParamList}
                          isEditOrCreate={!viewMode}
                          onQueryItemChange={onQueryItemChange}
                          onQueryItemDelete={onQueryItemDelete}
                        />
                      )}
                    {shouldDisplayHeaderParam() && (
                      <HeaderItemWrapper
                        key={`header-${editFormKey}`}
                        handleInputChange={handleInputChange}
                        serviceOpsFormData={serviceOpsFormData}
                        onAddHeaderHandler={onAddHeaderHandler}
                        headerParamList={headerParamList}
                        isEditOrCreate={!viewMode}
                        onHeaderItemChange={onHeaderItemChange}
                        onHeaderItemDelete={onHeaderItemDelete}
                        shouldDisplayQueryParam={shouldDisplayQueryParam}
                        shouldDisplayHeaderParam={shouldDisplayHeaderParam}
                        updateHeaderParamList={updateHeaderParamList}
                      />
                    )}
                  </>
                )}
              {shouldDisplayRequestResponseAttributes() && (
              <RequestResponseAttribute
                key={editFormKey}
                handleInputChange={handleInputChange}
                serviceOpsFormData={serviceOpsFormData}
                isEditOrCreate={!viewMode}
                requestAttributeList={requestAttributeList}
                responseAttributeList={responseAttributeList}
                setRequestAttributeList={requestDataUpdateHandler}
                setResponseAttributeList={responseDataUpdateHandler}
              />
                  )}
            </Form>
          </Row>
        </div>
      )}
    </div>
  );
};
export default ServiceOperationForm;
